<template>
  <form v-if="allTradingPairs" @submit.prevent="" class="needs-validation" novalidate>

    <div class="row mb-3 justify-content-center">
      <div class="col">
        <div class="form-floating">
          <input v-model="form.title" @change="validateForm()" class="form-control" type="text" placeholder="Title">
          <label for="floatingInput">Title</label>
          <div v-if="formValidations.title" class="invalid-feedback d-block fs-10">{{ formValidations.title }}</div>
        </div>
      </div>
    </div>

    <div class="row mb-3">
      <div class="col">
        <SingleSelectAutocomplete
          v-model="tempForm.tradingPair"
          :options="allTradingPairs"
          :option-label="pair => pair.baseCoin + ' / ' + pair.quoteCoin"
          :option-key="pair => pair.symbol"
          @update:model-value="validateForm()"
        >
          <template #label>
            Trading Pair <span v-if="loadingTradingPairs">(Loading...)</span>
          </template>
        </SingleSelectAutocomplete>

        <p class="mb-0 fs-10">
          <span>Assets on this account: </span>
          <span v-if="loadingAssets">Loading...</span>
          <span v-if="!loadingAssets && botAssetsBalance && botAssetsBalance.length <= 0 && getExchangeTradingAccount() == 'spot'" class="text-warning">This Bot will not be able to trade if you don't put at least one of the Trading Pair assets into the connected exchange account. Make sure the funds are on the "Trading/Spot/Unified Trading" account. The "Funding" account cannot trade and thus those funds are not visible here.</span>
          <span v-if="!loadingAssets && botAssetsBalance && botAssetsBalance.length <= 0 && getExchangeTradingAccount() != 'spot' && !getBotPosition" class="text-warning">This Bot will not be able to trade if you don't provide the collateral needed for opening positions.</span>
          <span v-if="!loadingAssets && botAssetsBalance && botAssetsBalance.length <= 0 && getExchangeTradingAccount() != 'spot' && getBotPosition" class="">Are all inside your open position.</span>
          <span v-if="!loadingAssets">
            <span v-for="elem in botAssetsBalance" :key="elem.coin">
              <span class="fw-bold">{{ elem.coin }}:</span> {{ getPrettyFloatNumber(elem.balance, 4) }} &nbsp;
            </span>
          </span>
          <br>
        </p>
        <div v-if="!loadingAssets && form.baseCoin && form.quoteCoin && getExchangeTradingAccount() == 'spot'" class="fs-10">This Bot will need <span class="fw-bold">{{ form.quoteCoin }}</span> for <span class="fw-bold">buy orders</span> and <span class="fw-bold">{{ form.baseCoin }}</span> for <span class="fw-bold">sell orders</span>.</div>
        <div v-else-if="!loadingAssets && form.baseCoin && form.quoteCoin && getExchangeTradingAccount() == 'perpetual' && getExchangeId() == 'hyperliquid'" class="fs-10">This Bot will use your USDC as collateral to open long & short positions.</div>
        <div v-else-if="!loadingAssets && form.baseCoin && form.quoteCoin && getExchangeTradingAccount() == 'perpetual'" class="fs-10">This Bot will use the collateral on your exchange account to open long & short positions.</div>

        <div v-if="formValidations.tradingPair" class="text-danger d-block fs-10 mt-1">{{ formValidations.tradingPair }}</div>
        <div v-if="!loadingAssets && !loadingTradingPairs && tradingPairAssetsWarningText" class="text-warning d-block fs-10 mt-1">{{ tradingPairAssetsWarningText }}</div>
        <div v-if="!loadingAssets && !loadingTradingPairs && getExchangeId() == 'mexc'" class="text-warning d-block fs-10 mt-1">MEXC does not allow certain trading pairs to be traded via their API (e.g. <a href="https://www.mexc.com/support/articles/15149585234969" target="_blank">BTC/USDT</a>, ETH/USDT and possibly others). That's why you won't find those pairs in the dropdown.</div>
        <div v-if="!loadingAssets && !loadingTradingPairs && getExchangeId() == 'cryptodotcom'" class="text-warning d-block fs-10 mt-1">Crypto.com does not trade fractional amounts for certain assets, like for example for XRP. If you send an order for 1.5 XRP, it will execute only for 1 XRP. This is true for the assets Crypto.com chooses and is subject to change on a daily basis.</div>
        <div v-if="!loadingAssets && !loadingTradingPairs && getExchangeId() == 'bybit' && form.baseCoin == 'SUI'" class="text-warning d-block fs-10 mt-1">On Bybit, you <span class="fw-bold">MUST approve Terms & Conditions before being able to trade SUI</span>. Switch into the subaccount, trade SUI manually to get that popup and accept the terms once. You'll be fine after that.</div>
      </div>
    </div>

    <div v-if="bot && getExchangeTradingAccount() == 'perpetual'" class="row mb-2">
      <div class="col text-center">

        <div class="text-start">
          <label>Leverage {{ form.leverage }}x/{{ tempForm.tradingPair?.maxLeverage }}x</label>
          <p class="fs-10 mb-1">
            This leverage setting is only valid for new positions. Existing positions use the leverage settings they already have.
          </p>
          <input v-model="form.leverage" @change="validateForm()" class="form-range" type="range" min="1" :max="tempForm.tradingPair?.maxLeverage" />
          <div v-if="formValidations.leverage" class="invalid-feedback d-block fs-10 mt-0 mb-1">{{ formValidations.leverage }}</div>
          <div v-if="getBotPosition && getBotPosition?.leverage != form.leverage" class="text-warning d-block fs-10 mt-1">
            The leverage of your open position ({{ getBotPosition?.leverage }}x) does not match with the leverage you have set in this Bot. It is advised to use the same leverage to avoid confusion about, which leverage will be used at the next trade.
          </div>
        </div>

      </div>
    </div>
    
    <div v-if="bot" class="row">
      <div class="col text-center">

        <div class="">
          <div class="text-start">
            <label>Order Size</label>
            <a href="https://youtu.be/vODuZmWFqxc" target="_blank" title="Video about how it works"><span class="fab fa-youtube" data-fa-transform="shrink-0_ grow-4 right-5 down-1"></span></a>
            <p class="fs-10">
              You choose the order size when you send the trading signal to SIGNUM (it's not a static setting).<br>
              Scroll down and pick the signal you need.<br>
            </p>
            <p v-if="getExchangeTradingAccount() != 'spot'" class="fs-10">Order size always refers to your collateral. For example, a 100% order size will use all of your collateral and the leverage will be applied to that value.</p>
          </div>
        </div>

      </div>
    </div>

    <div v-if="bot" class="row mt-1">
      <div class="col">
        <div class="text-start">
          <label>Allow tools & IPs to trigger this Bot</label>
        </div>

        <div class="form-check form-check-inline">
          <input v-model="form.ipAllowedTradingView" class="form-check-input" type="checkbox" />
          <label class="form-check-label" >TradingView</label>
        </div>

        <div class="form-check form-check-inline">
          <input v-model="form.ipAllowedMake" class="form-check-input" type="checkbox" />
          <label class="form-check-label" >Make</label>
        </div>

      </div>
    </div>

    <div v-if="bot" class="row mt-0 pt-0">
      <div class="col">
        <div class="form-check">
          <input v-model="form.ipAllowedCustom" class="form-check-input" type="checkbox" />
          <label class="form-check-label" >Allow custom IPs to trigger this Bot</label>

          <div v-if="form.ipAllowedCustom" class="mb-3">
            <textarea v-model="form.ipAllowedCustomList" class="form-control" style="height: 100px" rows=6></textarea>
            <p class="fs-10 mb-0">Comma separated (e.g. 172.217.1.1, 203.0.113.255, 191.10.123.1)</p>
            <div v-if="formValidations.ipAllowedCustomList" class="invalid-feedback d-block fs-10">{{ formValidations.ipAllowedCustomList }}</div>
          </div>
        </div>
        <div v-if="formValidations.ipAllowedError" class="invalid-feedback d-block fs-10">{{ formValidations.ipAllowedError }}</div>
      </div>
    </div>

    <div v-if="bot && userIsAutoTradingOrTelegramAdminMix" class="row mb-3 justify-content-center">
      <div class="col">
        <div class="form-floating">
          <input v-model="form.category" class="form-control" type="text" placeholder="Category">
          <label for="floatingInput">Category</label>
          <div v-if="formValidations.category" class="invalid-feedback d-block fs-10">{{ formValidations.category }}</div>
        </div>
      </div>
    </div>

    <div v-if="bot && !loading" class="row mt-4">
      <div class="col-4 d-flex justify-content-start">
        <button @click="back" type="submit" class="btn btn-secondary">Back</button>&nbsp;
      </div>
      <div class="col-8 d-flex justify-content-end">
        <button @click="save(true)" type="submit" class="btn btn-primary">Save</button>&nbsp;
        <!-- <button @click="save(false)" type="submit" class="btn btn-primary">Save & Back</button> -->
      </div>
    </div>

    <div v-if="!bot && !loading" class="row mt-3">
      <div class="col-4 d-flex justify-content-start">
        <button @click="cancel" type="submit" class="btn btn-secondary">Cancel</button>&nbsp;
      </div>
      <div class="col-8 d-flex justify-content-end">
        <button @click="save(false)" type="submit" class="btn btn-primary">Create Bot</button>
      </div>
    </div>

    <div v-if="loading" class="row text-end mt-3">
      <div class="col-12">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
    </div>

  </form>
</template>

<script>
import { userHelperMixin } from '@/mixins/userHelperMixin'
import SingleSelectAutocomplete from '@/components/SingleSelectAutocomplete.vue'

export default {
  mixins: [userHelperMixin],
  components: { SingleSelectAutocomplete },
  props: {
    loading: {
      required: true,
      type: Boolean
    },
    // For Edit Bot
    bot: {
      required: false,
      type: Object
    },
    // For Create Bot
    exchangeId: {
      required: false,
      type: String
    },
    exchangeTradingAccount: {
      required: false,
      type: String
    },
    apiKey: {
      required: false,
      type: String
    },
    apiSecret: {
      required: false,
      type: String
    },
    apiPassphrase: {
      required: false,
      type: String
    }
  },
  data () {
    return {
      loadingAssets: false,
      loadingTradingPairs: false,
      tempForm: {
        tradingPair: null // Object
      },
      form: {
        id: this.bot?.id,
        title: this.bot?.title,
        symbol: this.bot?.symbol,
        baseCoin: this.bot?.baseCoin,
        quoteCoin: this.bot?.quoteCoin,
        leverage: this.bot?.leverage,
        ipAllowedTradingView: this.bot?.ipAllowedTradingView,
        ipAllowedMake: this.bot?.ipAllowedMake,
        ipAllowedCustom: (this.bot?.ipAllowedCustomList != '') ? true : false,
        ipAllowedCustomList: this.bot?.ipAllowedCustomList,
        category: this.bot?.category
      },
      formValidations: {
      }
    }
  },
  computed: {
    allTradingPairs () {
      // if (this.$store.state.exchangeTradingPairs[this.getExchangeId()] && this.botAssetsBalance) { // Forgot why I filtered also by this.botAssetsBalance
      if (this.$store.state.exchangeTradingPairs[this.getExchangeId()] && this.$store.state.exchangeTradingPairs[this.getExchangeId()][this.getExchangeTradingAccount()]) {
        let tradingPairs = this.$store.state.exchangeTradingPairs[this.getExchangeId()][this.getExchangeTradingAccount()]

        // INFO: This was supposed to be removed when our API fixes this but I have the feelign this will protect us also in the future, so I'll leave it in.
        if (this.getExchangeId() == 'bybit' && this.getExchangeTradingAccount() == 'perpetual') {
          tradingPairs = tradingPairs.filter(pair => !pair.symbol.includes('-')) // // Filter out trading pairs with a dash in the symbol name
          tradingPairs = tradingPairs.filter(pair => pair.symbol == (pair.baseCoin + pair.quoteCoin)) // Filter all symbols that don't match the base+quote naming convention (otherwise our further logic breaks)
        }

        tradingPairs.sort((a,b) => (a.baseCoin > b.baseCoin) ? 1 : ((b.baseCoin > a.baseCoin) ? -1 : 0))

        return tradingPairs
      }
      return []
    },
    botAssetsBalance () {
      if (this.bot) { // Edit Mode
        let allBotAssets = this.$store.getters.getUserBotAssets
        if (allBotAssets && allBotAssets[this.bot.id]) return allBotAssets[this.bot.id].balance // Avoid an error we reloading the page by checking if not null
      } else { // Create mode
        let allBotAssets = this.$store.getters.getUserBotAssets
        if (allBotAssets && allBotAssets[0]) return allBotAssets[0].balance // At 0 we store the temporary bot assets until the bot get's an ID from the API
      }

      return null
    },
    botAssetsPositions () {
      if (this.bot) { // Edit Mode
        let allBotAssets = this.$store.getters.getUserBotAssets
        if (allBotAssets && allBotAssets[this.bot.id]) return allBotAssets[this.bot.id].positions // Avoid an error we reloading the page by checking if not null
      } else { // Create mode
        let allBotAssets = this.$store.getters.getUserBotAssets
        if (allBotAssets && allBotAssets[0]) return allBotAssets[0].positions // At 0 we store the temporary bot assets until the bot get's an ID from the API
      }

      return null
    },
    getBotPosition () {
      // Get the position, which matches the trading pair of this bot (it matters)
      return this.botAssetsPositions?.find(elem => elem.baseCoin+'' === this.form.baseCoin+'')
    },
    tradingPairAssetsWarningText() {
      let warning = ""
      if (!this.botAssetsBalance) return warning
      if (this.botAssetsBalance.length <= 0) return warning

      let assetFound = false
      for (const asset of this.botAssetsBalance) {
        if (asset.coin == this.form.baseCoin || asset.coin == this.form.quoteCoin) {
          assetFound = true
          break;
        }
      }
      // Show this message only when the user has chosen a tradin pair - this looks better when creating a bot
      if (!assetFound && this.tempForm.tradingPair?.symbol) warning = "The trading pair you have chosen, does not match any asset you have on this account. This bot will not be able to trade!"

      return warning
    },
    protectPrivacy () {
      return this.$store.getters.getProtectPrivacy
    }
  },
  methods: {
    getExchangeId () {
      if (this.bot?.exchangeId) return this.bot?.exchangeId // Fetch it from the bot object (edit bot)
      if (this.exchangeId) return this.exchangeId // Fetch it from the props (create bot)
      return null
    },
    getExchangeTradingAccount () {
      if (this.bot?.tradingAccount) return this.bot?.tradingAccount // Fetch it from the bot object (edit bot)
      if (this.exchangeTradingAccount) return this.exchangeTradingAccount // Fetch it from the props (create bot)
      return null
    },
    getPrettyFloatNumber (number, numDecimals) {
      let formattedNumber = parseFloat(number).toLocaleString('en-US', {minimumFractionDigits: numDecimals, maximumFractionDigits: numDecimals})

      if (this.protectPrivacy) {
        formattedNumber = 'x.xxxx'
      }

      return formattedNumber
    },
    validateForm () {
      let formValid = true
      this.formValidations = {} // Reset validations

      // Initialize form and tempForm
      if (!this.tempForm.tradingPair) {
        const tradingPair = this.allTradingPairs.find(elem => elem.symbol === this.bot?.baseCoin+''+this.bot?.quoteCoin)
        if (tradingPair) {
          this.tempForm.tradingPair = tradingPair
        } else {
          this.form.baseCoin = ''  
          this.form.quoteCoin = ''
        }
      }
      if (this.tempForm.tradingPair) {
        // Now take the values from tempForm and update the actual form with it
        this.form.baseCoin = this.tempForm.tradingPair.baseCoin
        this.form.quoteCoin = this.tempForm.tradingPair.quoteCoin
        this.form.symbol = this.tempForm.tradingPair.symbol
      }

      if (!this.form.leverage) {
        this.form.leverage = this.bot?.leverage
      }

      // Validate that the title is valid
      if (!this.form.title) {
        this.formValidations.title = 'Please give your bot a title.'
        formValid = false
      }

      // Validate that the coins are still available  
      if (!this.form.baseCoin || !this.form.quoteCoin) {
        if (this.bot) { // Edit mode
          this.formValidations.tradingPair = 'This trading pair '+this.bot?.baseCoin+' / '+this.bot?.quoteCoin+' is not available anymore in the connected exchange. Please pick a new one!'
        } else { // Create mode
          this.formValidations.tradingPair = 'Please choose a trading pair.'
        }
        formValid = false
      }

      // Validate the leverage setting
      this.form.leverage = parseInt(this.form.leverage)
      if (this.form.leverage > this.tempForm.tradingPair?.maxLeverage) {
        this.formValidations.leverage = 'The leverage is too high for the trading pair you have selected. Please choose a lower value.'
        formValid = false
      }

      // Validate if any IP/Tool is allowed to call this bot
      // Clear the IP list when the checkbox is unchecked while the textarea still has a value inside
      if (!this.form.ipAllowedCustom) {
        this.form.ipAllowedCustomList = ''
      }

      // Ensure that at least one IP option is enabled
      if (!this.form.ipAllowedTradingView
      && !this.form.ipAllowedMake
      && this.form.ipAllowedCustomList == '') {
        this.formValidations.ipAllowedError = 'Pick a least one way this Bot can be triggered otherwise no tool can trigger it.'
        formValid = false
      }

      // Ensure the IP list contains actual IPs
      if (this.form.ipAllowedCustomList && this.form.ipAllowedCustomList != '') {
        const regex = /\b(([0-9]{1,3}\.){3}[0-9]{1,3})((?!=|,).)?\b/g
        const validIPv4Addresses = this.form.ipAllowedCustomList.match(regex);

        // Remove spaces from the IP list so we can validate it
        const sanitizedIpV4List = this.form.ipAllowedCustomList.replace(/\s/g, '');
        let validIpCsvList = ''

        // Now prepare the valid IP list string so we can compare with the user input
        if (validIPv4Addresses && validIPv4Addresses.length > 0) {
          validIpCsvList = validIPv4Addresses.join(',')
        }

        if (sanitizedIpV4List != validIpCsvList) {
          this.formValidations.ipAllowedCustomList = 'Some IPs were not valid, please check your IP list for mistakes.'
          formValid = false
        }
      }

      // Remove spaces
      if (this.form.category) {
         this.form.category = this.form.category.replace(/\s/g, '')
      }
      
      return formValid
    },
    async save (stay) {
      if (!this.validateForm()) {
        return false
      }

      let newBot = {
        ...this.form
      }

      this.$emit("save", { newBot, stay });
    },
    back () {
      this.$router.push({ name: 'MyBots' })
    },
    cancel () {
      this.$router.push({ name: 'MyBots' })
    }
  },
  async created () {
  },
  async mounted () {
    const exchangeId = this.getExchangeId()
    const exchangeTradingAccount = this.getExchangeTradingAccount()

    // Show loading for both otherwise it looks strange in UI
    this.loadingTradingPairs = true
    this.loadingAssets = true

    // Fetch trading pairs only if they are not already in store
    if (!this.$store.state.exchangeTradingPairs[exchangeId] || !this.$store.state.exchangeTradingPairs[exchangeId][exchangeTradingAccount]) {
      if (this.bot) { // Edit mode
        await this.$store.dispatch('getExchangeTradingPairs', { exchangeId: exchangeId, tradingAccount: exchangeTradingAccount, botId: this.bot?.id })
      } else { // Create mode
        await this.$store.dispatch('getExchangeTradingPairsByApiKeys', { exchangeId: exchangeId, tradingAccount: exchangeTradingAccount, apiKey: this.apiKey, apiSecret: this.apiSecret, apiPassphrase: this.apiPassphrase })
      }
    }

    if (this.bot) { // Edit mode
      // Fetch assets data every time, so that it's always up to date!
      await this.$store.dispatch('getBotAssets', { exchangeId: exchangeId, botId: this.bot?.id })
    } else { // Create mode
      await this.$store.dispatch('getBotAssetsByApiKeys', { exchangeId: exchangeId, exchangeTradingAccount: exchangeTradingAccount, apiKey: this.apiKey, apiSecret: this.apiSecret, apiPassphrase: this.apiPassphrase })
    }

    this.loadingTradingPairs = false
    this.loadingAssets = false

    // Validate from the start so the user can see issues without having to make a change first
    this.validateForm()
  },
  unmounted () {
    // Reset data to be sure it's clean when editing the next bot
    this.loadingAssets = false
    this.loadingTradingPairs = false
    this.tempForm = {}
    this.form = {}
    this.formValidations = {}
  }
}
</script>

<style scoped>
</style>